<template>
  <div
    id="chart-canvas"
    class="gigafactories--chart flex flex-col items-start justify-start"
  >
    <div
      class="flex w-full flex-grow flex-col gap-x-3 border-solid border-s-300 pb-3 lg:gap-x-3 lg:pb-5 xl:flex-row xl:border-b-[3px]"
    >
      <div
        class="mb-3 hidden flex-grow text-base font-semibold lg:mb-0 lg:flex lg:items-center lg:text-2xl lg:font-bold"
      >
        {{ subtitle }}
      </div>
    </div>

    <!-- Desktop Content -->
    <div class="mt-5 w-full xl:gap-x-11 grid xl:grid-cols-2 xl:grid-rows-2">
      <div
        id="legend-container"
        class="mx-4 mb-6 flex flex-wrap justify-center gap-4 sm:mx-auto lg:mb-8 lg:mt-0"
      />
      <FilterLayout
        class="remove-me xl:order-2"
        filter-box-title="Regions"
        search-box-title="Companies"
        benchmark-verified-title="Benchmark Verified"
        :show-benchmark-verified-box="isLithiumIonBatteries"
        :show-button-select-all="showButtonSelectAll"
        :is-title-large="isTotalLabelLarge"
        @filter-change="searchChange"
        @select-all="selectAll"
      >
        <template #filterBox>
          <div v-for="region in regions" :key="region.name" class="mb-1">
            <label class="flex cursor-pointer items-center" :for="region.name">
              <input
                :id="region.name"
                v-model="region.selected"
                type="checkbox"
                :class="inputCheckboxClasses"
              />
              <span class="text-sm font-semibold text-white">
                {{ region.name }}
              </span>
            </label>
          </div>
        </template>

        <template #searchBoxMultiSelect>
          <div
            v-for="company in filterCompanies"
            :key="company.name"
            class="mb-1 max-h-[40vh] lg:max-h-[65vh]"
          >
            <label
              class="ml-3 flex cursor-pointer items-center"
              :for="company.name"
            >
              <input
                :id="company.name"
                v-model="company.selected"
                type="checkbox"
                :class="inputCheckboxClasses"
              />
              <span
                class="line-clamp-2 text-sm font-semibold text-white"
                :title="company.name"
              >
                {{ company.name }}
              </span>
            </label>
          </div>
        </template>

        <template #benchmarkMultiSelect>
          <div
            v-for="state in status"
            :key="state.slug"
            class="mb-1 max-h-[40vh] lg:max-h-[65vh]"
          >
            <label
              class="ml-3 flex cursor-pointer items-center gap-x-2"
              :for="state.label"
            >
              <input
                :id="state.label"
                v-model="state.selected"
                type="checkbox"
                :class="inputCheckboxClasses"
              />
              <span class="min-w-[160px] text-sm font-semibold text-white">
                {{ state.label }}
              </span>
              <component :is="statusIcons[state.slug]" class="h-4 w-auto" />
            </label>
          </div>
        </template>
      </FilterLayout>
      <GigafactoryData summary-box-title="Gigafactories" class="w-full">
        <template #summaryBox>
          <div class="flex flex-row gap-1">
            <div
              class="flex-grow p-4 pb-0 text-sm font-semibold text-p-em-high lg:py-4 xl:px-6"
            >
              <div>Active:</div>
              <div>Planning / Construction:</div>
              <div>{{ labelTotal }}</div>
              <div>{{ filteredCapacityLabel }} ({{ unitActive }}):</div>
            </div>
            <div
              class="basis-[120px] bg-s-300 p-4 pb-0 text-right text-sm font-bold text-p-em-high lg:py-4 xl:px-6"
            >
              <div>{{ activeGigafactories.length }}</div>
              <div>{{ inactiveGigafactories }}</div>
              <div>{{ selectedGigafactoriesLength }}</div>
              <div>{{ total }}</div>
            </div>
          </div>
        </template>
      </GigafactoryData>
    </div>

    <div class="flex w-full flex-col lg:mx-0">
      <div
        :key="titleChart"
        class="mx-4 my-10 flex justify-center text-center text-sm font-normal lg:mb-5 lg:mt-0 lg:text-sm lg:font-semibold"
      >
        {{ titleChart }}
      </div>
      <div v-if="showChartPagination" class="mb-6 flex justify-center">
        <Pagination
          :total-records="selectedGigafactories.length"
          :page-size="pageSize"
          :current-page="currentPage"
          @page-change="updatePage"
        />
      </div>
      <div
        class="relative grid"
        :class="[isLithiumIonBatteries ? 'grid-cols-2' : 'grid-cols-1']"
      >
        <div
          v-if="isLithiumIonBatteries"
          class="mt-[85px] flex flex-col justify-around text-end"
        >
          <div
            v-for="(item, index) in getLabels"
            :key="index"
            class="flex items-center justify-end gap-6 text-end text-xxs lg:text-base"
          >
            <span class="line-clamp-1"> {{ item.location }} </span>
            <div
              class="hidden min-h-[16px] basis-4 lg:flex lg:min-h-[28px] lg:basis-7 lg:items-center"
            >
              <component
                :is="statusIcons[item.status]"
                class="h-4 w-4 lg:h-6 lg:w-6"
              />
            </div>
          </div>
        </div>

        <div>
          <BarChart
            id="horizontal-bar-char-wrapper"
            class="relative z-10"
            :class="{ 'w-full': hideYLabel }"
            :style="{
              height: `${chartHeight}px`,
            }"
            :height="chartHeight"
            :chart-data="data"
            :options="options"
            :plugins="plugins"
          />
        </div>

        <BenchmarkWatermark
          left-class="left-[75%]"
          top-class="top-1/2"
          width-class="w-1/2"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/** packages */
import { Decimal } from 'decimal.js'
import uniqBy from 'lodash/uniqBy'
import intersection from 'lodash/intersection'

/** components */
import BarChart from '~/components/BarChart.vue'
import GigafactoryData from '~/components/capacity-database/GigafactoriesData.vue'
import FilterLayout from '~/components/capacity-database/FilterLayout.vue'
import BenchmarkWatermark from '~/components/BenchmarkWatermark.vue'
import Pagination from '~/components/common/Pagination.vue'

/** tailwind */
import resolveConfig from 'tailwindcss/resolveConfig'
import tailwindConfig from '~/tailwind.config'

/** utils */
import { generateCapacityLegends } from '~/utils/chart/customLegends/capacityLegends'
import {
  getLastYear,
  getOptionsChart,
  getYears,
  getYearStates,
} from '~/src/utils/capacity-database'
import { numberFormatter } from '~/utils/number'
import {
  CD_BG_CLASS_DEFAULT,
  CD_COLORS,
  CD_STATUS,
  CD_TEXT_CLASS_DEFAULT,
} from '~/src/constants/capacity-database/common'
import { updateYearStates } from '~/utils/chart/capacity-database'

/** types */
import type { TCDData, TCDRegion } from '~/src/types/capacity-database'
import type { TGeneral } from '~/src/types/common'

const fullConfig = resolveConfig(tailwindConfig) as TGeneral

/** props */
const props = withDefaults(
  defineProps<{
    showSlot?: boolean
    rawData?: Array<any>
    title?: string
    subtitle?: string
    icon?: string
    product?: string
    colors?: string[]
    checkClassColors?: string[]
    circleClassColors?: string[]
    capacityUnit?: string | null
    decimalPlaces?: number
    labelTotal?: string
    hideYLabel?: boolean
    isLithiumIonBatteries?: boolean
    truncateLabels?: boolean
    substrLength?: number
    regions?: TCDRegion[]
  }>(),
  {
    showSlot: false,
    rawData: () => [],
    title: '',
    subtitle: '',
    icon: '',
    product: '',
    colors: () => CD_COLORS,
    checkClassColors: () => CD_TEXT_CLASS_DEFAULT,
    circleClassColors: () => CD_BG_CLASS_DEFAULT,
    capacityUnit: null,
    decimalPlaces: 0,
    labelTotal: 'Total Producers:',
    hideYLabel: false,
    isLithiumIonBatteries: false,
    truncateLabels: false,
    substrLength: 45,
    regions: () => [
      { name: 'Asia (excl China)', selected: true },
      { name: 'Europe', selected: true },
      { name: 'North America', selected: true },
      { name: 'Other', selected: true },
      { name: 'China', selected: true },
    ],
  },
)

/** app */
const { $browser, $rudderstack } = useNuxtApp()

/** vars constants */
const ONE_HUNDRED = 100
const CHART_HEIGHT = 800
const PAGE_SIZE = 10
const DEFAULT_PAGE = 1
const INPUT_CHECKBOX_CLASSES =
  'form-checkbox mr-2 h-4 w-4 cursor-pointer rounded-sm text-p-500 focus:shadow-none focus:ring-0 focus:ring-offset-0'

/** refs */
const chartHeight = ref(CHART_HEIGHT)
const allCompanies = ref([])
const filterCompanies = ref([])
const selectedAll = ref(true)
const status = ref(CD_STATUS)
const plugins = ref([generateCapacityLegends])
const currentPage = ref(DEFAULT_PAGE)
const companies = ref([])
const pageSize = ref(PAGE_SIZE)
const inputCheckboxClasses = ref(INPUT_CHECKBOX_CLASSES)
const statusIcons = ref({
  operational: shallowRef(
    defineAsyncComponent(() => import('~/assets/icons/Satellite.svg')),
  ),
  construction: shallowRef(
    defineAsyncComponent(() => import('~/assets/icons/Construction.svg')),
  ),
  not_yet_assessed: shallowRef(
    defineAsyncComponent(() => import('~/assets/icons/NotYetAssessed.svg')),
  ),
})

const years = ref(getYears(props.rawData[0]?.capacities))
const yearStates = ref(getYearStates(years.value))
const lastYear = ref(getLastYear(years.value))

const data = ref<TCDData>({
  labels: [],
  datasets: [],
})

/** computed */
const showButtonSelectAll = computed(() => companies.value.length > 0)
const options = computed(() =>
  getOptionsChart(
    props.capacityUnit,
    props.hideYLabel,
    props.truncateLabels,
    props.substrLength,
    fullConfig.theme.colors.p.em.high,
    data.value.labels,
  ),
)
const showChartPagination = computed(
  () => $browser.isApple() && $browser.isMobile(),
)
const total = computed(() => {
  let totalCapacity = new Decimal(0)
  const availableYears = Object.keys(yearStates.value).filter(
    (year) => yearStates.value[year],
  )
  selectedGigafactories.value.forEach((gigafactory) => {
    const historicalCapacity = gigafactory.capacities
    availableYears.forEach((year) => {
      historicalCapacity.forEach((capacity) => {
        if (capacity.label === year) {
          totalCapacity = totalCapacity.plus(capacity.value)
        }
      })
    })
  })
  return numberFormatter(totalCapacity, props.decimalPlaces)
})

const selectedGigafactories = computed(() => {
  return (props.rawData || []).filter((_capacity) => {
    const _regions = selectedRegions.value.map((region) => region.name)
    const _companies = activeCompanies.value.map((company) => company.name)
    if (props.isLithiumIonBatteries) {
      const _status = selectedStatus.value.map((state) => state.slug)
      return (
        _regions.includes(_capacity.region) &&
        _companies.includes(_capacity.company) &&
        _status.includes(_capacity.status.slug)
      )
    }
    return (
      _regions.includes(_capacity.region) &&
      _companies.includes(_capacity.company)
    )
  })
})

const selectedRegions = computed(() =>
  props.regions.filter((region) => region.selected),
)

const activeCompanies = computed(() =>
  filterCompanies.value.filter((company) => company.selected),
)

const selectedStatus = computed(() =>
  status.value.filter((state) => state.selected),
)

const isTotalLabelLarge = computed(() => total.value.length > 5)

const activeGigafactories = computed(() =>
  selectedGigafactories.value.filter(
    (gigafactory) => gigafactory.capacities[0].value > 0,
  ),
)

const trackChartInteraction = () => {
  $rudderstack.trackChartInteraction({
    chart_title: props.subtitle,
    chart_type: 'bars',
    current_filters: {
      selectedRegions: selectedRegions.value,
      activeCompanies: activeCompanies.value,
    },
  })
}

const trackFilterApplied = () => {
  $rudderstack.trackFilterApplied({
    chart_title: props.subtitle,
    filter_type: 'price_data_filters', // there is no date filters here.
    filter_values: {
      selectedRegions: selectedRegions.value,
      activeCompanies: activeCompanies.value,
    },
  })
}

watch(selectedRegions, () => {
  trackFilterApplied()
})

watch(activeCompanies, () => {
  trackFilterApplied()
})

const inactiveGigafactories = computed(
  () => selectedGigafactories.value.length - activeGigafactories.value.length,
)

const selectedGigafactoriesLength = computed(
  () => uniqBy(selectedGigafactories.value, 'id').length,
)

const filteredCapacityLabel = computed(() => {
  const years = Object.values(yearStates.value)
  const filtered = years.filter((x) => !x).length
  if (filtered >= 2 && filtered < 3) {
    return 'Selected Capacity'
  }
  const _lastYear = lastYear.value ? `by ${lastYear.value}` : ''
  if (
    selectedRegions.value.length !== props.regions.length ||
    activeCompanies.value.length !== allCompanies.value.length
  ) {
    return `Selected Capacity ${_lastYear}`
  }
  return `Total Capacity ${_lastYear}`
})

const startIndex = computed(() => (currentPage.value - 1) * pageSize.value)
const endIndex = computed(() => {
  return showChartPagination.value
    ? startIndex.value + pageSize.value
    : selectedGigafactories.value.length
})

const getLabels = computed(() => {
  return selectedGigafactories.value
    .slice(startIndex.value, endIndex.value)
    .map((gigafactory) => {
      return {
        location: gigafactory.location,
        status: gigafactory.status.slug,
      }
    })
})

const unitActive = computed(() => props.capacityUnit)

const titleChart = computed(() => {
  const _data = Object.entries(yearStates.value)
  const _labels = _data.filter((item) => item[1]).sort()
  if (_labels.length === 0) {
    return 'You must select the least one year'
  }
  const _labelYear = _labels.map((year) => year[0].split(' ')[0])
  const _prefix =
    _labelYear.length === 1 || _labelYear.length === 0 ? ' by' : ', by'
  const _auxiliary = _labelYear.length >= 2 ? ', and' : ''
  const labelsFinal = `${_prefix} ${_labelYear
    .slice(0, -1)
    .join(', ')}${_auxiliary} ${_labelYear.slice(-1)}`

  return `${props.product} capacity estimates${labelsFinal}`
})

/** methods */
const configureGraph = () => {
  const datasets = years.value.map((year, index) => ({
    axis: 'y',
    label: year,
    backgroundColor: props.colors[index],
    checkClassColor: props.checkClassColors[index],
    circleClassColor: props.circleClassColors[index],
    data: getDataCollectionFromYear(year),
    unit: unitActive.value,
    decimalPlaces: props.decimalPlaces,
    barThickness: 24,
    hidden: !yearStates.value[year],
  }))
  const labels = selectedGigafactories.value
    .slice(startIndex.value, endIndex.value)
    .map((gigafactory) => {
      const strLength = gigafactory.location.length
      if (window.innerWidth >= 768 || strLength < 30) {
        return gigafactory.location
      }
      if (showChartPagination.value) {
        return `${gigafactory.location.substr(0, 30)}...`
      }
      const items = gigafactory.location.split(',')
      return [items.shift(), items.join(', ')]
    })
  data.value = { labels, datasets }
  /**
   * We reduce the height of the chart to avoid this error on iPhone:
   * "Canvas area exceeds the maximum limit (width * height > 16777216)"
   */
  chartHeight.value =
    150 +
    selectedGigafactories.value.slice(startIndex.value, endIndex.value).length *
      30
}

const getDataCollectionFromYear = (year: string) => {
  const dataCollection = []
  selectedGigafactories.value
    .slice(startIndex.value, endIndex.value)
    .forEach((gigafactory) => {
      gigafactory.capacities.forEach((capacity) => {
        if (capacity.label === year) {
          dataCollection.push(capacity.value)
        }
      })
    })
  return dataCollection
}

const selectAll = () => {
  companies.value.forEach((company) => (company.selected = !selectedAll.value))
  selectedAll.value = !selectedAll.value
}

const setCompanyBasedOnActiveRegions = (activeRegions: TCDRegion[]) => {
  companies.value = allCompanies.value.filter((company) => {
    return intersection(
      company.regions,
      activeRegions.map((region) => region.name),
    ).length
  })
  filterCompanies.value = companies.value
}

const extractCompanies = () => {
  const _uniqueCompanies = uniqBy(props.rawData, 'company').map(
    (company) => company.company,
  )
  companies.value = _uniqueCompanies.map((companyName) => {
    const _companies = props.rawData.filter(
      (company) => company.company === companyName,
    )
    return {
      name: companyName,
      selected: true,
      regions: _companies.map((company) => company.region),
    }
  })
  /**
   * This is to handle a kind of cache for every time the regions filter
   * changes, that gives the advantage of not doing this calculation again
   */
  allCompanies.value = companies.value
  filterCompanies.value = companies.value
}

const updateChartState = (toggledYear: string) => {
  /*
    Toggles the state of the years in an incremental way:
    - When a user enables a year, all previous years will be enabled too.
    - When a user disables a year, all upcoming years will be disabled too.
  */
  yearStates.value = updateYearStates(yearStates.value, toggledYear)
  configureGraph()
}

const searchChange = (search: string) => {
  const _filter = companies.value.filter((company) => {
    return company.name.toLowerCase().includes(search.toLowerCase())
  })
  filterCompanies.value = [..._filter]
}

const updatePage = (page: number) => {
  currentPage.value = page
  configureGraph()
}

/** watch */
watch(selectedRegions, (activeRegions: TCDRegion[]) => {
  setCompanyBasedOnActiveRegions(activeRegions)
  if (selectedGigafactories.value.length <= ONE_HUNDRED) {
    currentPage.value = 1
  }
  configureGraph()
})

watch(activeCompanies, () => {
  if (selectedGigafactories.value.length <= ONE_HUNDRED) {
    currentPage.value = 1
  }
  configureGraph()
})

watch(status, () => {
  if (selectedGigafactories.value.length <= ONE_HUNDRED) {
    currentPage.value = 1
  }
  configureGraph()
})

watch(
  yearStates,
  (newYears) => {
    const _data = Object.entries(newYears)
    const _dataFiltered = _data.filter((item) => item[1]).sort()
    const _lastYear = _dataFiltered[_dataFiltered.length - 1]?.at(0)
    lastYear.value = _lastYear?.split(' ')[0]
  },
  { deep: true },
)

/** lifecycle */
onMounted(() => {
  EventBus.$on('toggleOnChart', updateChartState)
  EventBus.$on('on-capacity-database-hover', trackChartInteraction)
  extractCompanies()
  configureGraph()
})

onBeforeUnmount(() => {
  EventBus.$off('toggleOnChart', updateChartState)
  EventBus.$off('on-capacity-database-hover', trackChartInteraction)
})
</script>
